import { Anchor } from '@components/atoms/Anchor';
import Hr from '@components/atoms/Hr';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import { Heading2, Paragraph } from '@components/atoms/Typography';
import BotExplanation from '@components/molecules/BotExplanation';
import BottomActionBar from '@components/molecules/BottomActionBar';
import ShowCrispUpperBottomActionBar from '@components/molecules/ShowCrispUpperBottomActionBar';
import { OfferId, UserIntent } from '@graphql/generated';
import BREAKPOINTS from '@lib/constants/breakpoints';
import COLORS from '@lib/constants/colors';
import {
  ConfirmQuoteFormResult,
  QuoteFinishForm,
} from '@lib/data/typesQuestions';
import { integerToCurrency } from '@lib/helpers/misc';
import GridBox from 'components/atoms/GridBox';
import React, { FC } from 'react';
import styled from 'styled-components';
import { rewriteCoverages } from '../sections/onboarding/CoveragesSection';
import DisclaimerSection from '../sections/onboarding/DisclaimerSection';
import TalkWithUs from '../sections/onboarding/TalkWithUsSection';

interface Props {
  onSubmit: (data: ConfirmQuoteFormResult) => void;
  loading: boolean;
  item: QuoteFinishForm;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  width: 100%;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  background: #ffffff;
  overflow: hidden;
  margin-bottom: 16px;
`;

const Title = styled(Paragraph)`
  font-weight: bold;
  margin-right: 16px;

  @media screen and (min-width: ${BREAKPOINTS.medium}) {
    font-size: 24px;
    margin-right: 32px;
  }
`;

const Description = styled.div`
  font-size: 14px;
  margin-top: 8px;
`;

const Container = styled.div`
  h2 {
    margin-bottom: 32px;
  }
`;

const PriceBox = styled.div`
  z-index: 1;

  padding: 32px;
  box-shadow: rgb(0 0 0 / 10%) 0px 16px 30px;
  border: 1px solid rgb(218, 218, 218);
  background: rgb(255, 255, 255);
  justify-content: center;
  border-radius: 10px;

  position: relative;
  width: auto;
  left: auto;
  top: auto;
  transform: none;
  max-width: 540px;
  margin: 32px auto 32px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    div {
      flex-direction: column;
    }
  }

  h2 {
    margin: 0 !important;
    font-size: 42px !important;

    span:first-child {
      margin-top: 16px;
      display: block;
      line-height: 1;
    }

    span {
      font-size: 16px;
    }
  }

  @media screen and (max-width: ${BREAKPOINTS.desk}) {
    h2 {
      font-size: 28px;
    }

    p {
      font-size: 14px;
    }
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    box-shadow: none;
    margin: 0 auto;
    border-radius: 0;
    border-bottom: none;
    border-left: none;
    border-right: none;
    max-width: none;
  }
`;

const ReviewCard: FC<{
  title?: string;
  description: React.ReactNode;
}> = ({ title, description }) => (
  <Wrapper>
    {title && (
      <GridBox
        alignItems="center"
        flexWrap="nowrap"
        justifyContent="space-between"
      >
        <Title>{title}</Title>
      </GridBox>
    )}
    <Description>{description}</Description>
  </Wrapper>
);

const getPlanName = (offerid: string) => {
  if (offerid === OfferId.SuhaiCompleto || offerid === 'suhaiCombo')
    return 'Seguro Roubo/Furto Total e de Perda Total por Colisão/Danos + Danos a Terceiros + Assistência 24h';
  if (offerid === OfferId.SuhaiPt)
    return 'Seguro Roubo/Furto Total e de Perda Total por Colisão/Danos + Assistência 24h';
  if (offerid === OfferId.SuhaiRoubo)
    return 'Seguro Roubo/Furto Total + Assistência 24h';
  if (offerid === OfferId.SuhaiRouboTerceiros)
    return 'Seguro Roubo/Furto Total + Danos a Terceiros + Assistência 24h';
  if (offerid === 'suhaiRouboSemAssistencia')
    return 'Roubo + Furto SEM ASSISTÊNCIA';

  return '';
};

const ConfirmQuoteForm: FC<Props> = ({ item, loading, onSubmit }) => {
  const installment = item.offer?.price?.installments?.find(
    (i) => i.number === item.installmentsNumber && i.method === item.method,
  );

  if (!installment) return null;

  const suhaiPartner = item.userIntent === UserIntent.SuhaiPartner;

  const planName = getPlanName(item?.offer?.id || '');

  return (
    <Container style={{ paddingBottom: 30 }}>
      <ShowCrispUpperBottomActionBar />
      {suhaiPartner && (
        <>
          <PriceBox>
            <Spacing vertical={2}>
              <GridBox>
                <Paragraph>Plano:</Paragraph>
                <strong>{planName}</strong>
              </GridBox>
            </Spacing>
            <Spacing vertical={2}>
              <GridBox>
                <Paragraph>Veículo:</Paragraph>
                <strong>{`${item?.vehicle?.brand} - ${item?.vehicle?.model} ${item?.vehicle?.modelYear}`}</strong>
              </GridBox>
            </Spacing>
            <Spacing vertical={2}>
              <GridBox>
                <Paragraph>Placa:</Paragraph>
                <strong>{item?.vehicle?.plate || 'Não tem ainda'}</strong>
              </GridBox>
            </Spacing>
            {item?.vehicle?.chassi && (
              <Spacing vertical={2}>
                <GridBox>
                  <Paragraph>Chassi:</Paragraph>
                  <strong>{item?.vehicle?.chassi}</strong>
                </GridBox>
              </Spacing>
            )}
            <Spacing vertical={2}>
              <GridBox>
                <Paragraph>Forma de pagamento: </Paragraph>
                <strong>
                  {` ${installment.number}x de ${integerToCurrency(
                    installment.amount,
                  )}`}
                </strong>
              </GridBox>
            </Spacing>

            <Paragraph style={{ color: COLORS.gray, fontSize: 14 }}>
              Ao finalizar a contratação declaro que estou de acordo com às
              condições gerais e regras do seguro
            </Paragraph>
            <br />
            <Anchor
              style={{ fontSize: 14 }}
              href="https://suhaiseguradora.com/wp-content/uploads/2022/02/CG-GERAL-AUTO_fevereiro-de-2022_atual_Suhai-Seguradora.pdf"
              target="_blank"
            >
              Condições Gerais
            </Anchor>
            <br />
            <Anchor
              style={{ fontSize: 14 }}
              href="https://suhaiseguradora.com/wp-content/uploads/2022/02/CG_Suhai_Maio-2021_Assistencia.pdf"
              target="_blank"
            >
              Condições gerais da Assistência 24h
            </Anchor>
          </PriceBox>
          <Section style={{ paddingTop: 0 }}>
            <BotExplanation>
              Aqui estão as coberturas selecionadas:
            </BotExplanation>

            {rewriteCoverages(item?.offer?.coverages || [], suhaiPartner).map(
              (c) => (
                <BotExplanation key={c.type}>
                  <strong>{c.name}</strong>
                  <br />
                  <br />
                  {c.description}
                </BotExplanation>
              ),
            )}
          </Section>
        </>
      )}

      {!suhaiPartner && (
        <>
          <PriceBox>
            <GridBox flexDirection="column">
              <Paragraph fontSize={14}>Valor do Seguro:</Paragraph>

              <Heading2>
                {`${item.installmentsNumber} x de `}
                {integerToCurrency(
                  item?.offer?.price.installments?.find(
                    (q) =>
                      q.number === item.installmentsNumber &&
                      q.method === item.method,
                  )?.amount || 0,
                )}
              </Heading2>
            </GridBox>
            <Paragraph>Coberturas:</Paragraph>
            {item.offer?.coverages.map((coverage) => (
              <strong style={{ display: 'block' }} key={coverage.type}>
                {coverage.name}
              </strong>
            ))}

            <Hr />
            <Spacing vertical={2}>
              <GridBox>
                <Paragraph>Veículo:</Paragraph>
                <strong>{`${item?.vehicle?.brand} - ${item?.vehicle?.model} ${item?.vehicle?.modelYear}`}</strong>
              </GridBox>
            </Spacing>
            <Spacing vertical={2}>
              <GridBox>
                <Paragraph>Placa:</Paragraph>
                <strong>{item?.vehicle?.plate || 'Não tem ainda'}</strong>
              </GridBox>
            </Spacing>
            {item?.vehicle?.chassi && (
              <Spacing vertical={2}>
                <GridBox>
                  <Paragraph>Chassi:</Paragraph>
                  <strong>{item?.vehicle?.chassi}</strong>
                </GridBox>
              </Spacing>
            )}
            <Spacing vertical={2}>
              <GridBox>
                <Paragraph>Forma de pagamento</Paragraph>
                <strong>
                  {` ${installment.number}x de ${integerToCurrency(
                    installment.amount,
                  )}`}
                </strong>
              </GridBox>
            </Spacing>

            <Paragraph style={{ color: COLORS.gray, fontSize: 14 }}>
              Ao finalizar a contratação confirmo que todas informações
              fornecidas são verdadeiras.
            </Paragraph>
          </PriceBox>
          <Section pastel>
            <Heading2>Coberturas</Heading2>
            {item.offer?.coverages.map((coverage) => (
              <ReviewCard
                title={coverage.name}
                description={<>{coverage.description}</>}
                // value={coverage.value}
                key={coverage.type}
              />
            ))}
          </Section>

          <TalkWithUs />
          <DisclaimerSection />
        </>
      )}
      <BottomActionBar
        buttonLabel="Contratar"
        smallText="Pagamento"
        text={`${installment.number}x de ${integerToCurrency(
          installment.amount,
        )}`}
        loading={loading}
        onClickOnAction={() => {
          onSubmit({ confirm: true });
        }}
      />
    </Container>
  );
};

export default ConfirmQuoteForm;
